import React from 'react'
import { node, string, bool } from 'prop-types'
import styled from 'styled-components'
import { stripUnit } from 'polished'
import { Link } from 'gatsby'

import * as spacing from 'styles/spacing'
import { media, mediaDown } from 'styles/media'
import { verticalPadding, borderRadius } from 'styles/global'
import { H2, H3, fontWeights, lineHeights } from 'styles/typography'
import { white } from 'styles/colors'

import Wrapper from 'components/Wrapper'
import Button from 'components/button/Button'

const Container = styled.section`
  position: relative;
  color: ${white};
  text-align: center;
  ${verticalPadding`3rem`};

  & + & {
    padding-top: 0;
  }

  ${media.small`
    ${verticalPadding`4rem`};
  `};

  ${media.medium`
    ${verticalPadding`6rem`};
  `};

  ${media.large`
    ${verticalPadding`7rem`};
  `};

  ${media.xLarge`
    ${verticalPadding`8rem`};
  `};
`

const Inner = styled.div`
  position: static;

  ${media.medium`
    display: flex;
    align-items: center;
    flex-direction: ${props => (props.isReverse ? 'row-reverse' : 'row')};
  `};
`

const Title = styled(H2)`
  color: ${white};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: ${fontWeights.regular};
  line-height: ${lineHeights.base};
  margin-bottom: ${spacing.large};

  ${media.medium`
    margin-bottom: ${spacing.xLarge};
  `};
`

const Content = styled.div`
  position: static;

  ${mediaDown.medium`
    max-width: 440px;
    margin-left: auto;
    margin-right: auto;
  `};

  ${media.medium`
    width: 60%;
    padding-left: ${props => (!props.isReverse ? '10%' : '0')};
    padding-right: ${props => (props.isReverse ? '10%' : '0')};
  `};

  ${media.large`
    width: 63%;
  `};
`

const Description = styled.div`
  font-size: 1.1em;
  margin-bottom: ${spacing.large};
  max-width: 580px;
  margin-left: auto;
  margin-right: auto;

  ${media.medium`
    font-size: 1.2em;
    margin-bottom: ${spacing.xLarge};

    p {
      margin-bottom: 1.75em;
    }
  `};

  ${media.large`
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.xLarge`
    margin-bottom: ${spacing.xxxLarge};
  `};
`

const Promo = styled(H3)`
  display: grid;
  grid-gap: ${spacing.xLarge};
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: ${spacing.large};
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  text-transform: uppercase;
  padding: 0 ${spacing.large};
  color: ${white};
  font-weight: ${fontWeights.regular};
  line-height: ${lineHeights.base};

  strong {
    display: block;
  }

  ${media.medium`
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.large`
    margin-bottom: ${spacing.xxLarge};
  `};
`

const FigureWrap = styled(Link)`
  display: block;
  margin-bottom: ${spacing.xLarge};

  ${mediaDown.medium`
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.medium`
    width: 40%;
  `};

  ${media.large`
    width: 37%;
  `};
`

const Figure = styled.img`
  position: static;
  border-radius: ${stripUnit(borderRadius) * 2 + 'px'};

  ${media.large`
    border-radius: ${stripUnit(borderRadius) * 3 + 'px'};
  `};
`

const StyledButton = styled(Button)`
  min-width: 250px;
`

const HomeSection = ({ figure, title, description, cta, link, promo, isReverse }) => (
  <Container>
    <Wrapper>
      <Inner isReverse={isReverse}>
        <FigureWrap to={link}>
          <Figure src={`${process.env.BASE_PATH}/images/${figure}`} />
        </FigureWrap>

        <Content isReverse={isReverse}>
          <Title>{title}</Title>
          <Description>{description}</Description>
          <Promo as="span">{promo}</Promo>

          <StyledButton to={link} theme="custom" customColor={white} variant="outline" size="large">
            {cta}
          </StyledButton>
        </Content>
      </Inner>
    </Wrapper>
  </Container>
)

HomeSection.propTypes = {
  figure: string.isRequired,
  title: string.isRequired,
  description: node.isRequired,
  promo: node.isRequired,
  cta: string.isRequired,
  link: string.isRequired,
  isReverse: bool
}

export default HomeSection
